import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Save, Trash2 } from "lucide-react";
import { Button, FormField as UiFormField, Input } from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useDealEditBundle, useUpdateDealEditBundle } from "@/hooks/deals/useDealDetail";
import { dealLabel } from "@/components/deals/dealFormat";
import { ApiError } from "@/api/client";
import { parseFormSchema, type FormField, type VisibleWhen } from "@/lib/formSchema";
import { normalizeDealSubmissionFinancialSchema } from "@/lib/dealSubmissionFinancialLabels";
import type {
  DealDetailDto,
  DealListItemDto,
  RealEstateIncluded,
  TransactionStatus,
  UpdateDealRequest,
} from "@/types/deals";
import type { DealStructuredIntakeSectionDto } from "@/types/intakeProfile";

const TRANSACTION_STATUSES: TransactionStatus[] = [
  "PendingLOI",
  "LOISigned",
  "PASigned",
  "InClosing",
  "Other",
];

const TRANSACTION_STATUS_LABEL: Record<TransactionStatus, string> = {
  PendingLOI: "Pending LOI",
  LOISigned: "LOI signed",
  PASigned: "PA signed",
  InClosing: "In closing",
  Other: "Other",
};

const REAL_ESTATE_OPTIONS: { value: RealEstateIncluded; label: string }[] = [
  { value: "None", label: "Not included" },
  { value: "Owned", label: "Included (owned)" },
  { value: "Leased", label: "Included (leased)" },
];

interface EditableStructuredSection extends DealStructuredIntakeSectionDto {
  data: Record<string, unknown>;
}

/**
 * Deal Pipeline spec §5.5 — Deal Edit page. Core Deal fields remain editable,
 * and broker-submission structured sections are now Deal-owned editable intake
 * details seeded by Create Deal.
 */
export function DealEditPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const bundleQuery = useDealEditBundle(dealId);
  const bundle = bundleQuery.data;
  const deal = bundle?.deal;
  const update = useUpdateDealEditBundle(dealId ?? "");

  const [form, setForm] = useState<UpdateDealRequest>(() => emptyDealForm());
  const [sections, setSections] = useState<EditableStructuredSection[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!bundle) return;
    setForm(toDealForm(bundle.deal));
    setSections(
      bundle.structuredSections.map((section) => ({
        ...section,
        data: parseData(section.dataJson),
      })),
    );
  }, [bundle]);

  const set = <K extends keyof UpdateDealRequest>(
    key: K,
    val: UpdateDealRequest[K],
  ) => setForm((f) => ({ ...f, [key]: val }));

  async function submit() {
    if (!dealId || !deal) return;
    setError(null);
    if (!form.businessNameInitial?.trim()) {
      setError("Deal name is required.");
      return;
    }
    if (!Number.isFinite(form.purchasePrice) || form.purchasePrice <= 0) {
      setError("Purchase price must be greater than zero.");
      return;
    }
    if (!Number.isFinite(form.sellerNoteAmount) || form.sellerNoteAmount < 0) {
      setError("Seller note must be zero or greater.");
      return;
    }
    try {
      await update.mutateAsync({
        deal: {
          ...form,
          businessNameInitial: form.businessNameInitial?.trim() || null,
          naicsInitial: form.naicsInitial?.trim() || null,
          dealNarrative: form.dealNarrative?.trim() || null,
          targetCloseDate: form.targetCloseDate || null,
          collateralType: form.collateralType?.trim() || null,
        },
        structuredSections: sections.map((section) => ({
          id: section.id,
          sourceSubmissionId: section.sourceSubmissionId,
          sourceSectionId: section.sourceSectionId,
          sectionTitle: section.sectionTitle,
          sectionOrder: section.sectionOrder,
          formSchemaJson: section.formSchemaJson,
          dataJson: JSON.stringify(section.data),
        })),
      });
      navigate(`/deal-flow/deals/${dealId}`);
    } catch (err) {
      setError(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Save failed.",
      );
    }
  }

  if (bundleQuery.isLoading) {
    return <p className="text-small text-gray-mid italic m-0">Loading deal…</p>;
  }
  if (!deal) {
    return (
      <p className="text-small text-gray-mid">
        Deal not found or you don&apos;t have permission to edit it.
      </p>
    );
  }

  const headlineLabel = dealLabel(deal as unknown as DealListItemDto);

  return (
    <>
      <PageHeader
        eyebrow={`Pipeline · ${deal.dealNumber}`}
        title={`Edit ${headlineLabel}`}
        description="Edit core deal fields and Deal-owned structured intake details copied from the broker submission. Business, Person, Entity, and document records are not changed by this form."
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => navigate(`/deal-flow/deals/${dealId}`)}
              disabled={update.isPending}
            >
              Cancel
            </Button>
            <Button onClick={submit} loading={update.isPending}>
              <Save size={16} aria-hidden /> Save deal
            </Button>
          </>
        }
      />

      <section className="bg-white-pure rounded-lg border border-border-light p-space-4">
        <h2 className="font-display text-h3 text-onyx m-0 mb-space-3">Core Deal Fields</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
          <UiFormField label="Deal name" required>
            <Input value={form.businessNameInitial ?? ""} onChange={(e) => set("businessNameInitial", e.target.value)} />
          </UiFormField>
          <UiFormField label="NAICS code">
            <Input value={form.naicsInitial ?? ""} onChange={(e) => set("naicsInitial", e.target.value)} maxLength={10} />
          </UiFormField>
          <UiFormField label="Purchase price (USD)" required>
            <Input type="number" min={0} step="1000" value={form.purchasePrice} onChange={(e) => set("purchasePrice", Number(e.target.value) || 0)} />
          </UiFormField>
          <UiFormField label="Seller note (USD)" required>
            <Input type="number" min={0} step="1000" value={form.sellerNoteAmount} onChange={(e) => set("sellerNoteAmount", Number(e.target.value) || 0)} />
          </UiFormField>
          <UiFormField label="Transaction status">
            <Select value={form.transactionStatus ?? ""} onChange={(e) => set("transactionStatus", e.target.value ? (e.target.value as TransactionStatus) : null)}>
              <option value="">— Not set —</option>
              {TRANSACTION_STATUSES.map((s) => <option key={s} value={s}>{TRANSACTION_STATUS_LABEL[s]}</option>)}
            </Select>
          </UiFormField>
          <UiFormField label="Target close date">
            <Input type="date" value={form.targetCloseDate ?? ""} onChange={(e) => set("targetCloseDate", e.target.value || null)} />
          </UiFormField>
          <UiFormField label="Cash at closing (USD)">
            <Input type="number" min={0} step="1000" value={form.cashAtClosing ?? ""} onChange={(e) => set("cashAtClosing", e.target.value === "" ? null : Number(e.target.value))} />
          </UiFormField>
          <UiFormField label="Subordinated debt (USD)">
            <Input type="number" min={0} step="1000" value={form.subordinatedDebtAmount ?? ""} onChange={(e) => set("subordinatedDebtAmount", e.target.value === "" ? null : Number(e.target.value))} />
          </UiFormField>
          <UiFormField label="Real estate included">
            <Select value={form.realEstateIncluded ?? ""} onChange={(e) => set("realEstateIncluded", e.target.value ? (e.target.value as RealEstateIncluded) : null)}>
              <option value="">— Not set —</option>
              {REAL_ESTATE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          </UiFormField>
          <UiFormField label="Collateral type">
            <Input value={form.collateralType ?? ""} onChange={(e) => set("collateralType", e.target.value)} />
          </UiFormField>
          <UiFormField label="Narrative" className="md:col-span-2">
            <Textarea rows={4} value={form.dealNarrative ?? ""} onChange={(e) => set("dealNarrative", e.target.value)} />
          </UiFormField>
        </div>
      </section>

      <StructuredIntakeEditor sections={sections} onChange={setSections} />

      {error && <p className="text-caption text-status-error mt-space-3" role="alert">{error}</p>}
    </>
  );
}

function StructuredIntakeEditor({
  sections,
  onChange,
}: {
  sections: EditableStructuredSection[];
  onChange: Dispatch<SetStateAction<EditableStructuredSection[]>>;
}) {
  if (sections.length === 0) {
    return (
      <section className="mt-space-4 bg-white-pure rounded-lg border border-border-light p-space-4">
        <h2 className="font-display text-h3 text-onyx m-0 mb-space-2">Broker Submission Fields</h2>
        <p className="text-small text-gray-mid m-0">No structured broker-submission fields are linked to this Deal yet.</p>
      </section>
    );
  }

  return (
    <section className="mt-space-4 flex flex-col gap-space-4">
      <div>
        <h2 className="font-display text-h3 text-onyx m-0">Broker Submission Fields</h2>
        <p className="text-small text-gray-mid m-0 mt-space-1">
          These Deal-owned fields were populated by Create Deal from the broker&apos;s structured submission.
        </p>
      </div>
      {sections.map((section, sectionIndex) => (
        <StructuredSectionCard
          key={section.id || `${section.sectionOrder}-${section.sectionTitle}`}
          section={section}
          onDataChange={(nextData) =>
            onChange((prev) => prev.map((item, idx) => idx === sectionIndex ? { ...item, data: nextData } : item))
          }
        />
      ))}
    </section>
  );
}

function StructuredSectionCard({
  section,
  onDataChange,
}: {
  section: EditableStructuredSection;
  onDataChange: (data: Record<string, unknown>) => void;
}) {
  const schema = useMemo(() => {
    const parsed = parseFormSchema(section.formSchemaJson);
    return parsed ? normalizeDealSubmissionFinancialSchema(parsed) : null;
  }, [section.formSchemaJson]);

  if (!schema) return null;
  const fields = schema.fields.filter((field) => shouldRenderField(field, section.data));
  if (fields.length === 0) return null;

  return (
    <div className="bg-white-pure rounded-lg border border-border-light p-space-4">
      <h3 className="font-display text-h4 text-onyx m-0 mb-space-3">{section.sectionTitle}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
        {fields.map((field) => (
          <StructuredFieldEditor
            key={field.name}
            field={field}
            value={section.data[field.name]}
            data={section.data}
            onChange={(value) => onDataChange({ ...section.data, [field.name]: value })}
          />
        ))}
      </div>
      <SectionNormalizationNotes section={section} fields={schema.fields} />
    </div>
  );
}

interface NormalizationMetaEntry {
  cleaned?: boolean;
  needsReview?: boolean;
}

/**
 * Surfaces the backend normalization result: wherever a structured value was
 * cleaned, show the broker's verbatim "as submitted" value; low-confidence
 * cleans carry a "needs review" flag. Read-only — the inputs above bind to the
 * cleaned working values.
 */
function SectionNormalizationNotes({
  section,
  fields,
}: {
  section: EditableStructuredSection;
  fields: FormField[];
}) {
  const meta = useMemo(
    () => parseRecord(section.normalizationMetaJson) as Record<string, NormalizationMetaEntry>,
    [section.normalizationMetaJson],
  );
  const submitted = useMemo(
    () => parseRecord(section.submittedDataJson),
    [section.submittedDataJson],
  );
  const paths = Object.keys(meta);
  if (paths.length === 0) return null;

  return (
    <ul className="mt-space-3 flex flex-col gap-space-1 list-none p-0 m-0 border-t border-border-light pt-space-2">
      {paths.map((path) => {
        const entry = meta[path];
        const parts = path.split(".");
        return (
          <li key={path} className="text-caption text-gray-mid">
            <span className="font-medium text-onyx">{labelForPath(fields, parts)}</span>
            {entry.cleaned && (
              <span> · cleaned — as submitted: “{displayValue(valueAtPath(submitted, parts))}”</span>
            )}
            {entry.needsReview && (
              <span
                className="ml-space-1 inline-block px-space-1 rounded-sm bg-status-warning/20 text-status-warning"
                data-testid={`needs-review-${path}`}
              >
                needs review
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function parseRecord(json: string | null | undefined): Record<string, unknown> {
  if (!json) return {};
  try {
    const parsed = JSON.parse(json) as unknown;
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function valueAtPath(root: unknown, parts: string[]): unknown {
  let cur: unknown = root;
  for (const part of parts) {
    if (cur === null || typeof cur !== "object") return undefined;
    const idx = Number(part);
    cur = Number.isInteger(idx) && Array.isArray(cur)
      ? (cur as unknown[])[idx]
      : (cur as Record<string, unknown>)[part];
  }
  return cur;
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function labelForPath(fields: FormField[], parts: string[]): string {
  const top = fields.find((f) => f.name === parts[0]);
  if (!top) return parts.join(" › ");
  if (parts.length === 1) return top.label;
  const rowNo = Number(parts[1]) + 1;
  const child = (top.fields ?? []).find((c) => c.name === parts[2]);
  return `${top.label} #${rowNo} · ${child?.label ?? parts[2]}`;
}

function StructuredFieldEditor({
  field,
  value,
  data,
  onChange,
}: {
  field: FormField;
  value: unknown;
  data: Record<string, unknown>;
  onChange: (value: unknown) => void;
}) {
  if (field.type === "repeatgroup") {
    const rows = Array.isArray(value) ? value.filter(isRecord) : [];
    const columns = (field.fields ?? []).filter((nested) => !isNonAnswerField(nested));
    return (
      <div className="md:col-span-2">
        <div className="font-display text-label font-semibold text-onyx mb-space-2">{field.label}</div>
        <div className="overflow-x-auto rounded-md border border-border-light">
          <table className="min-w-full text-small">
            <thead className="bg-white-warm">
              <tr>
                {columns.map((column) => (
                  <th key={column.name} className="text-left font-display text-caption uppercase tracking-label text-gray-mid px-space-2 py-space-2">{column.label}</th>
                ))}
                <th className="px-space-2 py-space-2"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-t border-border-light align-top">
                  {columns.map((column) => (
                    <td key={column.name} className="px-space-2 py-space-2 min-w-40">
                      <CompactFieldInput
                        field={column}
                        value={row[column.name]}
                        data={row}
                        onChange={(next) => {
                          const nextRows = [...rows];
                          nextRows[rowIndex] = { ...row, [column.name]: next };
                          onChange(nextRows);
                        }}
                      />
                    </td>
                  ))}
                  <td className="px-space-2 py-space-2 text-right">
                    <button
                      type="button"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border-light hover:bg-white-warm"
                      aria-label={`Remove ${field.label} row ${rowIndex + 1}`}
                      onClick={() => onChange(rows.filter((_, idx) => idx !== rowIndex))}
                    >
                      <Trash2 size={14} aria-hidden />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Button type="button" variant="secondary" className="mt-space-2" onClick={() => onChange([...rows, emptyRepeatRow(columns)])}>
          <Plus size={14} aria-hidden /> {field.addAnotherLabel || `Add ${field.label}`}
        </Button>
      </div>
    );
  }

  return (
    <UiFormField label={field.label} className={field.type === "textarea" ? "md:col-span-2" : undefined}>
      <CompactFieldInput field={field} value={value} data={data} onChange={onChange} />
      {field.helpText && <p className="text-caption text-gray-mid m-0 mt-space-1">{field.helpText}</p>}
    </UiFormField>
  );
}

function CompactFieldInput({
  field,
  value,
  data: _data,
  onChange,
}: {
  field: FormField;
  value: unknown;
  data: Record<string, unknown>;
  onChange: (value: unknown) => void;
}) {
  if (field.type === "textarea") {
    return <Textarea value={stringValue(value)} onChange={(e) => onChange(e.target.value)} rows={3} />;
  }
  if (field.type === "dropdown" || field.type === "radio") {
    return (
      <Select value={stringValue(value)} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">— Not set —</option>
        {(field.options ?? []).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </Select>
    );
  }
  if (field.type === "yesno" || field.type === "checkbox") {
    return (
      <Select value={typeof value === "boolean" ? String(value) : ""} onChange={(e) => onChange(e.target.value === "" ? null : e.target.value === "true")}>
        <option value="">— Not set —</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </Select>
    );
  }
  if (["currency", "number", "percentage"].includes(field.type)) {
    return <Input type="number" value={numberInputValue(value)} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} />;
  }
  if (field.type === "date" || field.type === "datesigned") {
    return <Input type="date" value={stringValue(value).slice(0, 10)} onChange={(e) => onChange(e.target.value || null)} />;
  }
  if (field.type === "multi-select") {
    return <Input value={Array.isArray(value) ? value.join(", ") : stringValue(value)} onChange={(e) => onChange(e.target.value.split(",").map((v) => v.trim()).filter(Boolean))} />;
  }
  return <Input type={field.type === "email" ? "email" : field.type === "phone" ? "tel" : "text"} value={stringValue(value)} onChange={(e) => onChange(e.target.value)} />;
}

function emptyDealForm(): UpdateDealRequest {
  return {
    purchasePrice: 0,
    sellerNoteAmount: 0,
    businessNameInitial: "",
    naicsInitial: "",
    transactionStatus: null,
    targetCloseDate: null,
    dealNarrative: "",
    cashAtClosing: null,
    subordinatedDebtAmount: null,
    realEstateIncluded: null,
    collateralType: "",
  };
}

function toDealForm(deal: DealDetailDto): UpdateDealRequest {
  return {
    purchasePrice: deal.purchasePrice,
    sellerNoteAmount: deal.sellerNoteAmount,
    businessNameInitial: deal.businessNameInitial ?? "",
    naicsInitial: deal.naicsInitial ?? "",
    transactionStatus: deal.transactionStatus ?? null,
    targetCloseDate: deal.targetCloseDate ? deal.targetCloseDate.slice(0, 10) : null,
    dealNarrative: deal.dealNarrative ?? "",
    cashAtClosing: deal.cashAtClosing ?? null,
    subordinatedDebtAmount: deal.subordinatedDebtAmount ?? null,
    realEstateIncluded: deal.realEstateIncluded ?? null,
    collateralType: deal.collateralType ?? "",
    // Counterparty sunset — carry the party FKs + businessId through the
    // full-replacement PUT so the save never detaches the counterparties.
    borrowerPartyId: deal.borrowerPartyId ?? null,
    sellerPartyId: deal.sellerPartyId ?? null,
    businessId: deal.businessId ?? null,
  };
}

function parseData(json: string | null | undefined): Record<string, unknown> {
  if (!json) return {};
  try {
    const parsed = JSON.parse(json) as unknown;
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function shouldRenderField(field: FormField, data: Record<string, unknown>): boolean {
  if (isNonAnswerField(field)) return false;
  return isVisible(field.visibleWhen, data);
}

function isNonAnswerField(field: FormField): boolean {
  return ["sectionheading", "instruction", "pagebreak", "fileattach", "signature", "initials"].includes(field.type);
}

function isVisible(rule: VisibleWhen | undefined, data: Record<string, unknown>): boolean {
  if (!rule) return true;
  const value = data[rule.field];
  if ("equals" in rule && rule.equals !== undefined) return value === rule.equals;
  if (rule.in && rule.in.length > 0) return rule.in.includes(value as string | number | boolean);
  return true;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

function numberInputValue(value: unknown): number | "" {
  return typeof value === "number" && Number.isFinite(value) ? value : "";
}

function emptyRepeatRow(columns: FormField[]): Record<string, unknown> {
  return Object.fromEntries(columns.map((column) => [column.name, null]));
}
